import React, { useEffect, useState, Suspense } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LayoutGrid, Users, LifeBuoy, UserCircle2 } from "lucide-react";

import { useAuth } from "@/context/AuthContext";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { haptic } from "@/lib/haptics";
import { cn } from "@/lib/utils";
import AccountSheet from "@/components/mobile/AccountSheet";

const ProfileModal = React.lazy(() => import("@/components/ProfileModal"));
const UpgradeModal = React.lazy(() => import("@/components/UpgradeModal"));

// Routes that are "root screens" of the tab bar. Detail screens (project,
// ticket…) push on top and show their own back button instead.
const TAB_ROUTES = ["/dashboard", "/team", "/support"];

const TABS = [
  { key: "projects", to: "/dashboard", icon: LayoutGrid, fallback: "Projets" },
  { key: "team", to: "/team", icon: Users, fallback: "Équipe" },
  { key: "help", to: "/support", icon: LifeBuoy, fallback: "Aide" },
];

/** True while a text field has focus → the on-screen keyboard is (about to be) open. */
function useTextInputFocused() {
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    const isText = (el) =>
      el &&
      ((el.tagName === "INPUT" &&
        !["checkbox", "radio", "button", "submit", "range", "color", "file"].includes(el.type)) ||
        el.tagName === "TEXTAREA" ||
        el.isContentEditable);
    const onIn = (e) => setFocused(!!isText(e.target));
    const onOut = () => setFocused(false);
    document.addEventListener("focusin", onIn);
    document.addEventListener("focusout", onOut);
    return () => {
      document.removeEventListener("focusin", onIn);
      document.removeEventListener("focusout", onOut);
    };
  }, []);
  return focused;
}

export default function BottomTabBar() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const keyboard = useTextInputFocused();
  const [accountOpen, setAccountOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  // Mount the heavy modals only after their first use.
  const [profileSeen, setProfileSeen] = useState(false);
  const [upgradeSeen, setUpgradeSeen] = useState(false);

  const visible = isMobile && !!user && TAB_ROUTES.includes(pathname.replace(/\/$/, ""));

  // Pages reserve room for the bar through a body class (see index.css).
  useEffect(() => {
    document.body.classList.toggle("has-tabbar", visible);
    return () => document.body.classList.remove("has-tabbar");
  }, [visible]);

  if (!visible) return null;

  return (
    <>
      <nav
        data-testid="bottom-tab-bar"
        aria-label={t("mobile.tabs.label", "Navigation principale")}
        className={cn(
          "fixed inset-x-0 bottom-0 z-40 border-t border-stone-200 bg-white/90 backdrop-blur-xl",
          "pb-safe pl-safe pr-safe transition-transform duration-200",
          keyboard && "translate-y-full"
        )}
      >
        <ul className="mx-auto flex h-[var(--tabbar-h)] max-w-md items-stretch">
          {TABS.map(({ key, to, icon: Icon, fallback }) => (
            <li key={key} className="flex-1">
              <NavLink
                to={to}
                end={to === "/dashboard"}
                data-testid={`tab-${key}`}
                onClick={() => haptic("tick")}
                className={({ isActive }) =>
                  cn(
                    "relative flex h-full flex-col items-center justify-center gap-0.5 text-[10.5px] font-semibold tracking-wide transition-colors",
                    isActive ? "text-neutral-950" : "text-neutral-400"
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute inset-x-0 top-0 mx-auto h-[2.5px] w-8 rounded-b-full bg-[#E05236]" />
                    )}
                    <Icon className="h-[22px] w-[22px]" strokeWidth={isActive ? 2.25 : 1.75} />
                    {t(`mobile.tabs.${key}`, fallback)}
                  </>
                )}
              </NavLink>
            </li>
          ))}
          <li className="flex-1">
            <button
              type="button"
              data-testid="tab-account"
              onClick={() => {
                haptic("tick");
                setAccountOpen(true);
              }}
              className="flex h-full w-full flex-col items-center justify-center gap-0.5 text-[10.5px] font-semibold tracking-wide text-neutral-400"
            >
              {user?.picture ? (
                <img src={user.picture} alt="" className="h-[22px] w-[22px] rounded-full object-cover" />
              ) : (
                <UserCircle2 className="h-[22px] w-[22px]" strokeWidth={1.75} />
              )}
              {t("mobile.tabs.account", "Compte")}
            </button>
          </li>
        </ul>
      </nav>

      <AccountSheet
        open={accountOpen}
        onOpenChange={setAccountOpen}
        onOpenProfile={() => {
          setProfileSeen(true);
          setProfileOpen(true);
        }}
        onOpenUpgrade={() => {
          setUpgradeSeen(true);
          setUpgradeOpen(true);
        }}
      />
      <Suspense fallback={null}>
        {profileSeen && (
          <ProfileModal
            open={profileOpen}
            onOpenChange={setProfileOpen}
            onUpgradeClick={() => {
              setUpgradeSeen(true);
              setUpgradeOpen(true);
            }}
          />
        )}
        {upgradeSeen && <UpgradeModal open={upgradeOpen} onOpenChange={setUpgradeOpen} />}
      </Suspense>
    </>
  );
}
